import { IAstOptions } from '../index.d';
import { IDeclarationObject, IFunctionObject } from '../../editor/index.d';
import { IPlaceholder } from '../../editor/index.d';
import { Comparisons } from '../constants/comparison';
import { IPosition, IToken } from '../typings/token';
import * as DeclarationMatch from '../declarations';
import { TokenGenerateError } from '../errors';
import { matchModel } from './model';
import { transformDeclarationsToObject, transformFunctionsToObject } from '../../helpers/transforms';
import { consumeProxies } from '../../helpers/proxies/consume';


function Position(start: number, end: number, line: number): IPosition {
  return { start, end, line };
}

const SymbolMapping: { [key: string]: string } = {
  ',': 'Comma',
  '(': 'OpenParenthesis',
  ')': 'CloseParenthesis',
  '[': 'OpenBracket',
  ']': 'CloseBracket',
  ';': 'EOF'
};

function matchContinuousSpecialCharacters(input: string) {
  const first = input[0];

  if (['+', '-', '*', '/', '%'].includes(first)) {
    return {
      type: 'Arithmetic',
      code: first,
      raw: first
    };
  }

  if (Object.keys(SymbolMapping).includes(first)) {
    return {
      type: SymbolMapping[first],
      code: first,
      raw: first
    };
  }

  const matched = input.match(/^([\<\>\=\+\-\*\/\%\&\|\!\,]+)/);
  if (matched) {
    const matchedString = matched[1];
    const isEndWithSubtract = matchedString.length > 1 && matchedString.endsWith('-');
    const raw = isEndWithSubtract ? matchedString.slice(0, matchedString.length - 1) : matchedString;
    const comparisons = Comparisons.filter(item => !!item.string);
    const matchedComparison = comparisons.find(item => item.string === raw);

    if (matchedComparison) {
      return {
        type: 'Comparison',
        code: matchedComparison.code,
        raw
      };
    }
    if (raw === '!') {
      return {
        type: 'LogicNot',
        raw,
        code: raw
      };
    }
    return { raw };
  }
}

function matchComparisonToken(input: string) {
  for (const comparison of Comparisons) {
    const matched = input.match(comparison.regexp!);
    if (matched) {
      return {
        type: 'Comparison',
        code: comparison.code,
        raw: matched[1]
      };
    }
  }
}

function matchCustomProperty(input: string) {
  const matched = input.match(/^({(\d+)})/);
  if (matched) {
    const raw = matched[1];
    return {
      type: 'CustomProperty',
      raw,
      isAtom: true,
      isCustomProperty: true,
      value: Number(matched[2])
    };
  }
}

function matchPlaceholder(input: string, placeholders: IPlaceholder[]) {
  for (const placeholder of placeholders) {
    const matched = input.match(placeholder.regexp);
    if (matched) {
      const raw = matched[1];
      return {
        placeholder,
        type: 'Placeholder',
        raw,
        isAtom: true,
        isPlaceholder: true
      };
    }
  }

}

function matchFqnProperty(input: string, fqn: string) {
  const matched = input.match(/^(this(\.([a-zA-Z_0-9](\[\])?)+)+)/);
  if (matched) {
    const path = matched[1];

    return {
      type: 'FqnProperty',
      raw: path,
      isAtom: true,
      isFqnProperty: true,
      dsl: `${fqn}(${path.slice(5)})`
    };
  }
}

function matchDeclaration(input: string, declarations: IDeclarationObject[]) {
  for (const declaration of declarations) {
    const Declaration = (DeclarationMatch as any)[declaration.code];
    if (Declaration) {
      const matched = Declaration.match(input);
      if (matched) return matched;
    }
  }
}

function matchAtom(input: string) {
  const matched = input.match(/^([a-zA-Z]+)/);
  if (matched) {
    if (['true', 'TRUE', 'false', 'FALSE'].includes(matched[1])) {
      return {
        type: 'Boolean',
        code: matched[1].toUpperCase(),
        raw: matched[1],
        isAtom: true
      };
    } else if (['NULL', 'null'].includes(matched[1])) {
      return {
        type: 'Null',
        code: matched[1].toUpperCase(),
        raw: matched[1],
        isAtom: true
      };
    } else if (['DESC', 'ASC', 'desc', 'asc'].includes(matched[1])) {
      return {
        type: 'Direction',
        code: matched[1].toUpperCase(),
        raw: matched[1],
        isAtom: true
      };
    }
  }
}

function matchFunction(input: string, functions: IFunctionObject[]) {
  const matched = input.match(/^([a-zA-Z_0-9]+)\(/i);
  if (matched) {
    const code = matched[1].toUpperCase();
    const func = functions.find((item) => item.code === code);
    const name = func ? func.name : code;
    return {
      type: 'Function',
      raw: matched[1],
      code,
      name,
      formatName: `\`${name}\``
    };
  }
}

function matchDigit(input: string, latestToken: IToken) {

  const digRegExp = !latestToken ||
    latestToken.type === 'Comparison' ||
    latestToken.type === 'Arithmetic' ||
    latestToken.type === 'Comma' ||
    latestToken.type === 'OpenParenthesis' ||
    latestToken.type === 'OpenBracket' ?
    /^(\-?[0-9\.]+)/ : /^([0-9\.]+)/;

  const matched = input.match(digRegExp);
  if (matched && !Number.isNaN(Number(matched[1]))) {
    const digit = matched[1];
    if (digit.startsWith('.') || digit.endsWith('.')) {
      throw { code: 'invalid-number', message: `无效的数字 \`${digit}\``, length: digit.length };
    }
    return {
      type: digit.includes('.') ? 'Number' : 'Integer',
      raw: digit,
      value: Number(digit),
      isAtom: true
    };
  }
}

function matchString(input: string) {
  const ch = input[0];
  if (ch === '\'' || ch === '"') {
    let i = 1;
    let escaped = false;

    while (i < input.length) {
      const char = input[i];
      if (char === ch && !escaped) {
        break;
      }

      escaped = !escaped && char === '\\';
      i++;
    }

    if (input[i] !== ch) {
      throw {
        code: 'no-string-end-character',
        message: '字符串缺少结束符',
        length: i
      };
    }

    const word = input.slice(0, i + 1);

    if (word.includes('\n')) {
      throw {
        code: 'invalid-string',
        message: '字符串中不能包含回车符，请用`\\n`替换回车',
        length: word.length
      };
    }


    return {
      type: 'String',
      value: word.slice(1, word.length - 1),
      raw: word,
      isAtom: true
    };
  }
}

export function generateTokens(originInput: string, options: IAstOptions, ignoreWhiteSpace = true) {
  const input = options.proxies ? consumeProxies(originInput, options.proxies) : originInput;

  const functions = transformFunctionsToObject(options.functions);
  const declarations = transformDeclarationsToObject(options.declarations);
  const placeholders = options.placeholders || [];

  const tokens: IToken[] = [];
  const errors: any[] = [];
  const length = input.length;
  let i = 0;
  let line = 0;

  while (i < length) {
    const ch = input[i];

    if (ch === ';') {
      i++;
      break;
    }

    if (ch === ' ' || ch === '\t') {
      if (!ignoreWhiteSpace) {
        tokens.push({
          type: 'WhiteSpace',
          raw: ch,
          position: Position(i, i + ch.length, line)
        });
      }
      i++;
      continue;
    }

    if (ch === '\n') {
      i++;
      line++;
      continue;
    }

    // 字面量数字类型
    try {
      const digitToken = matchDigit(input.slice(i), tokens[tokens.length - 1]);
      if (digitToken) {
        const position = Position(i, i + digitToken.raw.length, line);
        const token = { ...digitToken, position };
        tokens.push(token);
        i += digitToken.raw.length;
        continue;
      }
    } catch (error) {
      const position = Position(i, i + error.length - 1, line);
      throw tokenGenerateError(error.code, position, error.message);
    }

    // 连续的特殊字符串
    const continuousSpecialCharacterToken = matchContinuousSpecialCharacters(input.slice(i));
    if (continuousSpecialCharacterToken) {
      const position = Position(i, i + continuousSpecialCharacterToken.raw.length, line);
      if (continuousSpecialCharacterToken.type) {
        tokens.push({ ...continuousSpecialCharacterToken, position });
        i += continuousSpecialCharacterToken.raw.length;
        continue;
      } else {

        throw tokenGenerateError('invalid-operator', position, '无效的操作符');
      }
    }

    // 类型声明，必须在函数匹配之前
    try {
      const declarationToken = matchDeclaration(input.slice(i), declarations);
      if (declarationToken) {
        const position = Position(i, i + declarationToken.raw.length, line);
        tokens.push({ ...declarationToken, position });
        i += declarationToken.raw.length;
        continue;
      }
    } catch (error) {
      const start = i + error.declarationString.indexOf(error.valueString);
      const end = start + error.valueString.length;
      const position = { line, start, end };
      errors.push({ ...error, position });
      i += error.declarationString.length;
      continue;
    }

    // 函数
    const functionToken = matchFunction(input.slice(i), functions);
    if (functionToken) {
      const position = Position(i, i + functionToken.raw.length, line);
      tokens.push({ ...functionToken, position });
      i += functionToken.raw.length;
      continue;
    }

    // 比较操作符
    const comparisonToken = matchComparisonToken(input.slice(i));
    if (comparisonToken) {
      const position = Position(i, i + comparisonToken.raw.length, line);
      tokens.push({ ...comparisonToken, position });
      i += comparisonToken.raw.length;
      continue;
    }

    // 自定义属性
    const customPropertyToken = matchCustomProperty(input.slice(i));
    if (customPropertyToken) {
      const position = Position(i, i + customPropertyToken.raw.length, line);
      tokens.push({ ...customPropertyToken, position });
      i += customPropertyToken.raw.length;
      continue;
    }

    const placeholderToken = matchPlaceholder(input.slice(i), placeholders);
    if (placeholderToken) {
      const position = Position(i, i + placeholderToken.raw.length, line);
      tokens.push({ ...placeholderToken, position });
      i += placeholderToken.raw.length;
      continue;
    }

    // fqn属性
    // 业务端不能直接使用 this.xxx 所以也不需要传入 fqn 值
    if (options.fqn && options.fqn.fqn) {
      const fqnPropertyToken = matchFqnProperty(input.slice(i), options.fqn.fqn);
      if (fqnPropertyToken) {
        const position = Position(i, i + fqnPropertyToken.raw.length, line);
        tokens.push({ ...fqnPropertyToken, position });
        i += fqnPropertyToken.raw.length;
        continue;
      }
    }

    // model
    const modelPropertyToken = matchModel(input.slice(i));
    if (modelPropertyToken) {
      const position = Position(i, i + modelPropertyToken.raw.length, line);
      tokens.push({ ...modelPropertyToken, position });
      i += modelPropertyToken.raw.length;
      continue;
    }


    // 原数据
    const atomToken = matchAtom(input.slice(i));
    if (atomToken) {
      const position = Position(i, i + atomToken.raw.length, line);
      tokens.push({ ...atomToken, position });
      i += atomToken.raw.length;
      continue;
    }

    // 字符串
    try {
      const stringToken = matchString(input.slice(i));
      if (stringToken) {
        const position = Position(i, i + stringToken.raw.length, line);
        tokens.push({ ...stringToken, position });
        i += stringToken.raw.length;
        continue;
      }
    } catch (error) {
      const position = Position(i, i + error.length, line);
      throw tokenGenerateError(error.code, position, error.message);
    }

    const word = input.slice(i).match(/^(\S+)/);
    if (word) {
      throw tokenGenerateError('invalid-literal-character', Position(i, i + word[1].length, line), `无效的字符 \`${word[1]}\``);
    }
  }

  if (errors.length > 0) {
    throw errors[0];
  }

  return tokens;
}


function tokenGenerateError(code: string, position: IPosition, message: string = '') {
  return new TokenGenerateError(code, message, position);
}
